import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Hash, Loader2, Plus, Pin, PinOff, Trash2, ExternalLink,
  Image as ImageIcon, X as XIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { SocialEmbed } from "../components/SocialEmbed";

const PLATFORM_OPTIONS = [
  { id: "instagram", label: "Instagram" },
  { id: "x", label: "X / Twitter" },
  { id: "tiktok", label: "TikTok" },
  { id: "threads", label: "Threads" },
  { id: "facebook", label: "Facebook" },
  { id: "bluesky", label: "Bluesky" },
];

const QUICK_LINKS = [
  {
    label: "Instagram · #PUSB",
    href: "https://www.instagram.com/explore/tags/pusb/",
  },
  {
    label: "X · #PUSB",
    href: "https://x.com/hashtag/PUSB?src=hashtag_click&f=live",
  },
  {
    label: "TikTok · #PUSB",
    href: "https://www.tiktok.com/tag/pusb",
  },
];

function detectPlatform(url) {
  const u = (url || "").toLowerCase();
  if (u.includes("instagram.com")) return "instagram";
  if (u.includes("x.com") || u.includes("twitter.com")) return "x";
  if (u.includes("tiktok.com")) return "tiktok";
  if (u.includes("threads.net") || u.includes("threads.com")) return "threads";
  if (u.includes("facebook.com") || u.includes("fb.com") || u.includes("fb.watch")) return "facebook";
  if (u.includes("bsky.app")) return "bluesky";
  return "";
}

export default function AdminSocial() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState("");
  const [caption, setCaption] = useState("");
  const [pinned, setPinned] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/social");
      setPosts(r.data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onUrlChange = (v) => {
    setUrl(v);
    if (!platform) {
      const detected = detectPlatform(v);
      if (detected) setPlatform(detected);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setAdding(true);
    try {
      await api.post("/social", {
        url: url.trim(),
        platform: platform || detectPlatform(url) || null,
        caption: caption.trim() || null,
        pinned,
      });
      setUrl("");
      setPlatform("");
      setCaption("");
      setPinned(false);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAdding(false);
    }
  };

  const togglePin = async (post) => {
    try {
      await api.put(`/social/${post.id}`, { pinned: !post.pinned });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (post) => {
    if (!window.confirm("Remove this social post from the wall?")) return;
    try {
      await api.delete(`/social/${post.id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]"
      data-testid="page-admin-social"
    >
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 font-display text-sm font-semibold"
            data-testid="admin-social-back"
          >
            <ArrowLeft size={14} /> Admin
          </Link>
          <span className="label-kicker text-[var(--brand-body)]">
            Social wall · {user?.name || user?.email}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <div className="mb-8 border-b border-black/10 pb-6">
          <div className="flex items-center gap-2 text-[#6CABDD]">
            <Hash size={16} strokeWidth={2.5} />
            <span className="label-kicker">#PUSB curator</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Curate the social wall.
          </h1>
          <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
            Spot a #PUSB post, copy its public URL, paste it below. The post
            stays on the original network — we just embed it.
          </p>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="quick-links">
            {QUICK_LINKS.map((q) => (
              <a
                key={q.href}
                href={q.href}
                target="_blank"
                rel="noreferrer"
                data-testid="quick-link"
                className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 label-kicker text-[var(--brand-ink)] hover:border-[#0A0F17]"
              >
                {q.label} <ExternalLink size={11} />
              </a>
            ))}
          </div>
        </div>

        {/* Add form */}
        <form
          onSubmit={submit}
          className="mb-10 rounded-md border border-black/10 bg-white p-6"
          data-testid="social-add-form"
        >
          <h2 className="font-display text-lg font-bold">Add a post</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <label className="block sm:col-span-2">
              <span className="label-kicker text-[var(--brand-body)]">Post URL</span>
              <input
                required
                type="url"
                value={url}
                onChange={(e) => onUrlChange(e.target.value)}
                placeholder="https://www.instagram.com/p/..."
                data-testid="social-add-url"
                className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono-sm text-sm outline-none focus:border-[#0A0F17]"
              />
            </label>
            <label className="block">
              <span className="label-kicker text-[var(--brand-body)]">Platform</span>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                data-testid="social-add-platform"
                className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
              >
                <option value="">Auto-detect</option>
                {PLATFORM_OPTIONS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="mt-4 block">
            <span className="label-kicker text-[var(--brand-body)]">
              Caption (optional, shown below the post)
            </span>
            <input
              type="text"
              maxLength={300}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="social-add-caption"
              placeholder="e.g. Mason-Clark on the title win"
              className="mt-2 block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
            />
          </label>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
                data-testid="social-add-pinned"
                className="h-4 w-4 accent-[#6CABDD]"
              />
              <span className="font-display font-semibold">Pin to top</span>
            </label>
            <button
              type="submit"
              disabled={adding}
              data-testid="social-add-submit"
              className="ml-auto inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-white disabled:opacity-60"
            >
              {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {adding ? "Adding…" : "Add to wall"}
            </button>
          </div>
          {error ? (
            <p
              className="mt-3 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
              data-testid="social-add-error"
            >
              {error}
            </p>
          ) : null}
        </form>

        {/* Existing posts */}
        <h2 className="mb-4 font-display text-lg font-bold">
          On the wall ({posts.length})
        </h2>
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {["s1","s2","s3"].map((id) => (
              <div
                key={id}
                className="h-64 animate-pulse rounded-md border border-black/10 bg-white"
              />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 bg-white p-10 text-center"
            data-testid="admin-social-empty"
          >
            <p className="font-display text-base font-bold">No posts on the wall yet.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Use one of the quick-link chips above to find a #PUSB post, then
              paste it in the form.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 gap-5 lg:grid-cols-2"
            data-testid="admin-social-list"
          >
            {posts.map((p) => (
              <div
                key={p.id}
                data-testid="admin-social-row"
                className="rounded-md border border-black/10 bg-white"
              >
                <SocialEmbed platform={p.platform} url={p.url} caption={p.caption} previewImage={p.preview_image} />
                {p.platform === "facebook" ? (
                  <FacebookImageEditor post={p} onSaved={load} />
                ) : null}
                <div className="flex items-center justify-between gap-2 border-t border-black/10 px-4 py-3">
                  <span className="label-kicker text-[var(--brand-body)]">
                    Added {new Date(p.created_at).toLocaleString("en-GB")}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => togglePin(p)}
                      data-testid="admin-social-pin"
                      className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
                    >
                      {p.pinned ? <PinOff size={12} /> : <Pin size={12} />}
                      {p.pinned ? "Unpin" : "Pin"}
                    </button>
                    <button
                      onClick={() => remove(p)}
                      data-testid="admin-social-delete"
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#E63946] px-3 py-1.5 label-kicker text-[#E63946] hover:bg-[#E63946] hover:text-white"
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}


/* Manual thumbnail upload for Facebook posts — Meta blocks server-side OG
   fetching so admins need to provide their own preview image to make the
   FB card rich. Other platforms (Instagram/X/TikTok/Threads/Bluesky)
   already provide official embed scripts so don't need this. */
function FacebookImageEditor({ post, onSaved }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put(`/social/${post.id}`, { preview_image: up.data.url });
      await onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clear = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.put(`/social/${post.id}`, { preview_image: "" });
      await onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-t border-dashed border-black/10 bg-[#F8FAFC] px-4 py-3"
      data-testid={`fb-image-editor-${post.id}`}
    >
      <span className="label-kicker text-[var(--brand-body)]">
        Facebook thumbnail (manual — Meta blocks auto-fetch)
      </span>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`fb-image-input-${post.id}`}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid={`fb-image-upload-${post.id}`}
        className="inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-2 py-1 text-xs hover:border-[#0A0F17] disabled:opacity-50"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
        {post.preview_image ? "Replace" : "Upload"}
      </button>
      {post.preview_image ? (
        <button
          type="button"
          onClick={clear}
          disabled={busy}
          data-testid={`fb-image-clear-${post.id}`}
          className="inline-flex items-center rounded-md border border-black/10 bg-white px-2 py-1 text-xs hover:border-[#E63946] hover:text-[#E63946]"
        >
          <XIcon size={12} />
        </button>
      ) : null}
      {err ? <span className="text-xs text-[#E63946]">{err}</span> : null}
    </div>
  );
}
